import _, { identity, orderBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Input } from 'reactstrap';
import { Set } from 'immutable';
import ScrollingTable from './ScrollingTable';
import { useItemContext } from '../../contexts/ItemContext';
import { TableMessage } from '../basics/Message';
import { StickyTh } from './StickyCell';
import StickyGroupTd from './StickyGroupTd';
import ActivatableTr from './ActivatableTr';
import TableCsvButton from './TableCsvButton';
import SearchIcon from '../icons/SearchIcon';
import { mobileMediaQuery } from '../../utils/responsive';
import { filterRowByColumnValue } from '../../utils/utils';
import DropdownButton from '../basics/DropdownButton';
import { ShrinkContent, ShrinkContentColumn } from '../ShrinkContent';
import { SubmitButton } from '../templates/template-components';

const returnsTrue = () => () => true;

const ItemsTableDiv = styled(ShrinkContentColumn)`
  width: max-content;
  max-width: 100%;
  height: 100%;
  overflow: hidden;
`;

export const TableWrapper = styled(ShrinkContent)``;

const getValue = (accessor, item) =>
  typeof accessor === 'function' ? accessor(item) : item[accessor];

const TableGroupRowsWithTotal = ({
  cols,
  items,
  colIndexesForTotals,
  rows,
}) => (
  <>
    {rows}

    <tr>
      {cols
        .slice(0)
        .map(({ displayString, accessor, format = identity, style }, i) => {
          let content = '';
          if (colIndexesForTotals.includes(i)) {
            const groupTotal = _.sum(
              items.map((item) => getValue(accessor, item)),
            );
            content = <strong>{format(groupTotal)}</strong>;
          } else if (i === 0) {
            // If we're not summing column 0, use it to say "Total":
            content = <strong>Total</strong>;
          }

          return (
            <td key={[displayString, accessor].join('-')} style={style}>
              {content}
            </td>
          );
        })}
    </tr>
  </>
);

const TableGroup = ({
  items,
  cols,
  groupName,
  hover,
  isTableHovered,
  onMouseEnter,
  stickyGroups,
  selectedIds = [],
  scrollProps = {},
}) => {
  const { activeKey, setActiveKey, clearActiveKey } = useItemContext();
  // Clear the active key on unmount:
  useEffect(() => clearActiveKey, []); // eslint-disable-line react-hooks/exhaustive-deps

  const colIndexesForTotals = cols
    .map((c, i) => [c.showGroupTotal, i])
    .filter(([showGroupTotal]) => showGroupTotal)
    .map(([, i]) => i);

  // Don't group total if there's only one item or if there are no columns w/ showGroupTotal
  const showGroupTotals = colIndexesForTotals.length > 0 && items.length > 1;

  // NOTE: when canShowGroupTotals is true, there's an extra row in the group, so we increment this.
  const groupLabelLength = items.length + (showGroupTotals ? 1 : 0);

  const rows = items.map((item, i) => {
    const baseKey = item.key || item.id;
    const key = groupName ? `${groupName}-${baseKey}` : baseKey;
    const isSelected = selectedIds?.includes(baseKey);
    const setActive = () => {
      // Also call onMouseEnter to set the table hovered in case
      // onMouseEnter was not called on the table, which may
      // happen when a modal was closed.
      onMouseEnter();
      setActiveKey(key);
    };
    const {
      colSpan: itemColSpan = {},
      format: itemFormat,
      rowSpan: itemRowSpan = {},
      skipCols: itemSkipCols = [],
      style: itemStyle,
    } = item;

    return (
      <ActivatableTr
        key={key}
        onMouseEnter={setActive}
        onFocus={setActive}
        onBlur={clearActiveKey}
        onMouseLeave={clearActiveKey}
        isActive={key === activeKey}
        isSelected={isSelected}
        activeColor={item.color}
        hover={hover}
        isTableHovered={isTableHovered}
        onClick={item.onClick}
        scrollProps={scrollProps}
      >
        {groupName &&
          (stickyGroups ? (
            <StickyGroupTd rowSpan={groupLabelLength} rowIdx={i}>
              {groupName}
            </StickyGroupTd>
          ) : (
            i === 0 && <td rowSpan={groupLabelLength}>{groupName}</td>
          ))}
        {cols
          .filter((c) => !itemSkipCols.includes(c.accessor))
          .map(
            ({
              accessor,
              displayString,
              format: columnFormat = identity,
              style,
            }) => {
              const value = getValue(accessor, item);
              return (
                <td
                  key={[displayString, accessor].join('-')}
                  colSpan={itemColSpan[accessor] || 1}
                  rowSpan={itemRowSpan[accessor] || 1}
                  style={itemStyle || style}
                >
                  {itemFormat
                    ? itemFormat(value, item)
                    : columnFormat(value, item)}
                </td>
              );
            },
          )}
      </ActivatableTr>
    );
  });

  if (!showGroupTotals || rows.length === 0) return rows;

  // Add a total under this group of rows:
  return (
    <TableGroupRowsWithTotal
      cols={cols}
      items={items}
      colIndexesForTotals={colIndexesForTotals}
      rows={rows}
    />
  );
};

const SEARCH_ICON_SIZE = 16;

const SearchInput = styled(Input)`
  width: 300px;
  padding-left: calc(${SEARCH_ICON_SIZE}px + 1rem);
`;

const SearchInputIcon = styled(SearchIcon)`
  position: absolute;
  top: 15px;
  left: 0;
  @media ${mobileMediaQuery} {
    top: 16px;
  }
`;

export const TableToolbar = styled.div`
  position: relative;
`;

const ItemsTable = ({
  data,
  /*
   * cols is an array of objects with the following properties:
   * - accessor: the key of the item to display
   * - displayString: the string to display in the table header
   *      Note: displayString can also be jsx, but you'll need to use an
   *      exportDisplayString for csv exports
   * - exportDisplayString: an override display string to use for the csv export
   * - format: a function that takes (value, item) and returns the formatted value
   * - csvFormat: an override function for formatting the value in the csv export
   * - thStyle: an object with css properties to apply to the th element
   * - style: an object with css properties to apply to the td elements
   * - hideIfAllMatchFn: if all item's values match the function, the column is hidden
   * - isSmall: if true, the column is smaller
   * - exportOnly: if true, the column is only shown in the export to csv
   * - showGroupTotal: if true, the table will show totals for this column under each group.
   */
  cols,
  msg,
  byCrop, // Supports data as array of either cropAggregates or crops when using byCrop
  getItems = identity,
  createButton,
  createButtonOnClick,
  saveButton,
  hover,
  searchable,
  showCount,
  search = returnsTrue,
  searchPlaceholder = 'Search',
  sortColumn,
  sortDirection = 'asc',
  children,
  tableHeader = <div />,
  csvFileName, // include to show the Export to CSV button
  csvButtonProps = {}, // Include to customize button styling
  csvButtonInToolbar = false, // Show CSV button in toolbar, not header
  isLineTable,
  className,
  selectedIds,
  filterableColumn = '',
  multiselectButton,
  multiselectOnClick,
  selectedItems,
  setSelectedItems,
  footer,
  scrollProps, // Customize scroll behavior of items in table
  fullWidth,
  ...tableProps
}) => {
  const [isTableHovered, setIsTableHovered] = useState(false);
  const onMouseEnter = useCallback(() => setIsTableHovered(true), []);
  const onMouseLeave = useCallback(() => setIsTableHovered(false), []);
  const [searchValue, setSearchValue] = useState('');
  const [columnFilterValue, setColumnFilterValue] = useState('All');
  const filterAndSort = useCallback(
    (items) => {
      const filteredItems = getItems(items)
        ?.filter((item) =>
          filterRowByColumnValue(item, filterableColumn, columnFilterValue),
        )
        .filter(search(searchValue));
      return sortColumn
        ? orderBy(filteredItems, sortColumn, sortDirection)
        : filteredItems;
    },
    [
      getItems,
      search,
      searchValue,
      filterableColumn,
      columnFilterValue,
      sortColumn,
      sortDirection,
    ],
  );

  const [columnOptions, filteredData, filteredRowCount] = useMemo(() => {
    if (!data) {
      return [null, null, 0];
    }

    const columnValues = [];

    const cropsOrAggregates = data.cropAggregates || data.crops;
    // If by crop, create a group for each crop, otherwise use a single unnamed group
    const filteredGroups = byCrop
      ? cropsOrAggregates?.map((cropOrAggregate) => {
          columnValues.push(getItems(cropOrAggregate)[filterableColumn]);
          return {
            groupName: cropOrAggregate.crop?.name || cropOrAggregate.name,
            items: filterAndSort(cropOrAggregate),
          };
        })
      : [{ items: filterAndSort(data) }];
    if (!byCrop) {
      const originalData = getItems(data);
      originalData.forEach((row) => columnValues.push(row[filterableColumn]));
    }
    return [
      new Set(columnValues).sort().map((value) => ({
        name: _.capitalize(value),
        key: value,
      })),
      filteredGroups || [],
      filteredGroups?.reduce((sum, group) => sum + group.items.length, 0),
    ];
  }, [data, byCrop, filterAndSort, getItems, filterableColumn]);

  // Only use sticky groups when there is at least one group with multiple items.
  const withStickyGroups = useMemo(() => {
    return (
      byCrop &&
      !!filteredData &&
      filteredData.some((group) => group.items.length > 1)
    );
  }, [byCrop, filteredData]);

  // The table is empty if all items are filtered.
  const isEmpty = useMemo(
    () =>
      !filteredData || filteredData?.every(({ items }) => items.length === 0),
    [filteredData],
  );

  const message = msg || (isEmpty ? 'No items found' : null);

  const nonEmptyCols = useMemo(
    () =>
      cols.filter(({ accessor, hideIfAllMatchFn }) => {
        return (
          !hideIfAllMatchFn || // always show if there's no function
          filteredData
            ?.flatMap(({ items }) => items)
            ?.some((item) => !hideIfAllMatchFn(getValue(accessor, item)))
        );
      }),
    [cols, filteredData],
  );

  const csvCols = useMemo(
    () =>
      !!csvFileName && byCrop
        ? [{ displayString: 'Crop', accessor: 'groupName' }, ...nonEmptyCols]
        : nonEmptyCols,
    [byCrop, nonEmptyCols, csvFileName],
  );

  const csvData = useMemo(() => {
    return (
      !!csvFileName &&
      filteredData?.flatMap(({ groupName, items }) =>
        items.map((item) => ({
          ...item,
          groupName,
        })),
      )
    );
  }, [csvFileName, filteredData]);

  const csvButton = csvFileName && !msg && csvData && csvData.length > 0 && (
    <TableCsvButton
      cols={csvCols}
      data={csvData}
      title={csvFileName}
      outline
      size="sm"
      className={`ml-${csvButtonInToolbar ? 0 : 4} my-2 text-muted`}
      {...csvButtonProps}
    />
  );

  const multiselectAllChecked = useMemo(() => {
    const filteredItems = filteredData?.flatMap(({ items = [] }) => items);
    return (
      !!filteredItems?.length &&
      filteredItems.every((item) => selectedItems?.has(item.id))
    );
  }, [filteredData, selectedItems]);

  const onMultiselectAll = useCallback(
    (e) => {
      if (e.target.checked) {
        setSelectedItems(
          new Set(
            filteredData
              ?.flatMap(({ items = [] }) => items)
              ?.map(({ id }) => id),
          ),
        );
      } else {
        setSelectedItems(new Set());
      }
    },
    [filteredData, setSelectedItems],
  );

  const onMultiselect = useCallback(
    (itemId) => {
      // Remove item id from selected if already present, otherwise add item id
      if (selectedItems.has(itemId)) {
        setSelectedItems(selectedItems.delete(itemId));
      } else {
        setSelectedItems(selectedItems.add(itemId));
      }
    },
    [selectedItems, setSelectedItems],
  );

  const formatMultiselectCol = useCallback(
    (itemId) => (
      <input
        type="checkbox"
        aria-label="select this item"
        checked={selectedItems?.has(itemId)}
        onClick={(e) => e.stopPropagation()}
        onChange={() => onMultiselect(itemId)}
      />
    ),
    [onMultiselect, selectedItems],
  );

  const multiselectCol = useMemo(
    () => ({
      accessor: 'id',
      displayString: (
        <input
          type="checkbox"
          id="multiselect-all"
          checked={multiselectAllChecked}
          onChange={onMultiselectAll}
          aria-label="select all items"
        />
      ),
      thStyle: { width: 'max-content', minWidth: 'max-content' },
      format: formatMultiselectCol,
    }),
    [multiselectAllChecked, onMultiselectAll, formatMultiselectCol],
  );

  const tableCols = useMemo(
    () => [
      ...(setSelectedItems ? [multiselectCol] : []),
      ...nonEmptyCols.filter(({ exportOnly }) => !exportOnly),
    ],
    [setSelectedItems, multiselectCol, nonEmptyCols],
  );

  return (
    <ItemsTableDiv className={`d-flex flex-column ${className}`}>
      {typeof tableHeader === 'function' ? (
        tableHeader({ csvButton })
      ) : (
        <div className="d-flex justify-content-between align-items-center">
          <div>{tableHeader}</div>
          {!csvButtonInToolbar && (
            <div className="d-flex align-items-end">{csvButton}</div>
          )}
        </div>
      )}
      {(searchable ||
        showCount ||
        createButton ||
        saveButton ||
        filterableColumn ||
        (selectedItems != null && selectedItems.size !== 0) ||
        (csvButtonInToolbar && csvButton)) && (
        <TableToolbar className="d-flex justify-content-between align-items-center flex-wrap">
          <div className="d-flex align-items-center flex-wrap">
            {searchable && (
              <div
                className={`position-relative my-2 ${
                  isLineTable ? 'mr-3' : 'mr-2'
                }`}
              >
                <SearchInput
                  bsSize="sm"
                  className="mr-2"
                  onChange={(e) => setSearchValue(e.target.value.toLowerCase())}
                  placeholder={searchPlaceholder}
                />
                <SearchInputIcon
                  className="position-absolute ml-2 mt-n2 text-muted"
                  width={SEARCH_ICON_SIZE}
                  height={SEARCH_ICON_SIZE}
                />
              </div>
            )}
            {createButton && (
              <Button
                color="primary"
                onClick={createButtonOnClick}
                className={`my-2 ${isLineTable ? 'mr-3' : 'mr-2'}`}
                size="sm"
              >
                {createButton} ＋
              </Button>
            )}
            {saveButton && (
              <SubmitButton
                className={`my-2 ${isLineTable ? 'mr-3' : 'mr-2'}`}
                size="sm"
              />
            )}
            {multiselectButton && selectedItems?.size !== 0 && (
              <Button
                color="primary"
                onClick={multiselectOnClick}
                className={`my-2 ${isLineTable ? 'mr-3' : 'mr-2'}`}
                size="sm"
              >
                {typeof multiselectButton === 'function'
                  ? multiselectButton()
                  : multiselectButton}
              </Button>
            )}
            {filterableColumn && (
              <DropdownButton
                placeholder="All"
                selectedOption={columnFilterValue}
                options={
                  columnOptions
                    ? [{ name: 'All', key: 'all' }, ...columnOptions]
                    : []
                }
                setOption={(option) => setColumnFilterValue(option.name)}
                className={`my-2 ${isLineTable ? 'mr-3' : 'mr-2'}`}
              />
            )}
            {csvButtonInToolbar && csvButton}
          </div>
          {showCount && filteredRowCount > 0 && (
            <div className="ml-2 my-2 text-nowrap">
              {filteredRowCount} {filteredRowCount === 1 ? 'item' : 'items'}
            </div>
          )}
        </TableToolbar>
      )}
      <TableWrapper>
        <ScrollingTable
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          hover={hover}
          $fullWidth={fullWidth}
          {...tableProps}
        >
          <thead>
            <tr>
              {byCrop && (
                <StickyTh
                  className="font-weight-bold"
                  $isLineTable={isLineTable}
                >
                  Crop
                </StickyTh>
              )}
              {tableCols.map(
                ({ displayString, accessor, thStyle, isSmall }) => (
                  <StickyTh
                    key={[displayString, accessor].join('-')}
                    style={thStyle}
                    $small={isSmall}
                    $isLineTable={isLineTable}
                  >
                    {displayString}
                  </StickyTh>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {message ? (
              <TableMessage colSpan={tableCols.length + 1}>
                {message}
              </TableMessage>
            ) : (
              filteredData.map(({ groupName, items }) => (
                <TableGroup
                  key={groupName || 'group'}
                  items={items}
                  cols={tableCols}
                  groupName={groupName}
                  hover={hover}
                  isTableHovered={isTableHovered}
                  stickyGroups={withStickyGroups}
                  onMouseEnter={onMouseEnter}
                  selectedIds={selectedIds}
                  scrollProps={scrollProps}
                />
              ))
            )}
          </tbody>
          {footer && <tfoot>{footer}</tfoot>}
        </ScrollingTable>
      </TableWrapper>
    </ItemsTableDiv>
  );
};

export const PaddedItemsTable = styled(ItemsTable)`
  table {
    margin-bottom: 0;
    padding-bottom: 1rem;
  }
`;

export default ItemsTable;
